import type { AsyncDataOptions, NuxtApp } from 'nuxt/app';
import type { Ref } from 'vue';
import type { Schema } from 'yup';
import type { ExcellenceProgramLevel } from '../types/enums';
import { object } from 'yup';
import { getApiErrorMessage, hasApiErrorData, isNetworkError, isTimeoutError } from './errorHelpers';

export function isDefined(value: unknown): value is NonNullable<typeof value> {
	return value !== undefined && value !== null;
}

export function isDefinedAndNotEmpty(value: unknown): value is NonNullable<typeof value> {
	if (Array.isArray(value)) {
		return isDefined(value) && value.length > 0;
	}

	return isDefined(value) && value !== '';
}

/**
 * Converts an empty string to null so a cleared input never submits `''`.
 * `null` and `undefined` pass through unchanged.
 */
export function emptyToNull<T extends string | number | null | undefined>(value: T): T | null {
	return value === '' ? null : value;
}

export type AllSchemaProperties<T> = {
	[K in keyof T]: Schema;
};

export function yupObject<T>(input: AllSchemaProperties<Partial<T>>) {
	return object(input);
}

export function toDateOnlyString(date?: Date | string | number | null | {
	year: number;
	month: number;
	day: number;
}) {
	if (!date) {
		return undefined;
	}

	let dateObj: Date;
	if (typeof date === 'string' || typeof date === 'number') {
		dateObj = new Date(date);
	}
	else if ('year' in date && 'month' in date && 'day' in date) {
		dateObj = new Date(date.year, date.month - 1, date.day);
	}
	else {
		dateObj = date;
	}

	// make sure that month and date start with 0 if they are less than 10
	const month = dateObj.getMonth() + 1 < 10 ? `0${dateObj.getMonth() + 1}` : dateObj.getMonth() + 1;
	const day = dateObj.getDate() < 10 ? `0${dateObj.getDate()}` : dateObj.getDate();

	// make sure year is 4 digits
	const year = dateObj.getFullYear().toString().padStart(4, '0');

	return `${year}-${month}-${day}`;
}

export function getFormattedDate(date?: Date | string | number | null) {
	if (!date) {
		return undefined;
	}

	let dateObj: Date;
	if (typeof date === 'string' || typeof date === 'number') {
		dateObj = new Date(date);
	}
	else {
		dateObj = date;
	}

	// January 7, 2027

	const options: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	return dateObj.toLocaleDateString('en-US', options);
}

export function parseApiErrorMessage(error: any): string {
	if (isTimeoutError(error)) {
		return 'Connection timed out. Please check your internet connection and try again.';
	}

	if (isNetworkError(error)) {
		return typeof navigator !== 'undefined' && navigator.onLine === false
			? 'You appear to be offline. Please check your connection and try again.'
			: "We couldn't reach the server. Please try again in a moment.";
	}

	if (hasApiErrorData(error)) {
		return getApiErrorMessage(error) || 'Something went wrong on our end. Please try again shortly.';
	}

	// Raw error.message is technical; it's logged in displayError, not shown to users.
	return 'Something went wrong. Please try again.';
}

// eslint-disable-next-line regexp/no-dupe-disjunctions
export const urlRegex = /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[\w-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?\w+=\w+(&\w+=\w+)*)?$/gm;

type DateLike = Date | string | number | null;
interface FormatDateOptions {
	includeTime?: boolean;
	includeDate?: boolean;
	includeSeconds?: boolean;
	joinStr: string;
}
export function formatDate(date: DateLike, opts: FormatDateOptions = {
	includeDate: true,
	includeTime: false,
	includeSeconds: false,
	joinStr: ' ',
}) {
	if (!isDefinedAndNotEmpty(date)) {
		return '';
	}

	const dateObj = new Date(date);

	const str = [];
	// We use format day/month/year with leading zeros: DD/MM/YYYY
	if (opts.includeDate) {
		str.push(`${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`);
	}
	// We use format hour:minutes:seconds or hours:minutes with leading zeros and 24 hour format: HH:MM:SS or HH:MM if we have without seconds
	if (opts.includeTime) {
		str.push(`${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}${opts.includeSeconds ? `:${dateObj.getSeconds().toString().padStart(2, '0')}` : ''}`);
	}

	return str.join(opts.joinStr);
}

export function getInitials(...names: string[]) {
	// Combine all names into a single string, split by spaces, and filter out empty parts
	const parts = names
		.flatMap(name => (name || '').split(' '))
		.map(part => part.trim())
		.filter(Boolean);

	if (parts.length === 0) {
		return undefined;
	}

	if (parts.length === 1) {
		return parts[0]?.[0]?.toUpperCase();
	}

	// Use first and last part for initials
	const firstPart = parts[0];
	const lastPart = parts[parts.length - 1];

	if (!firstPart || !lastPart || !firstPart[0] || !lastPart[0]) {
		return undefined;
	}

	return (firstPart[0] + lastPart[0]).toUpperCase();
}

export function yupNumberTransformer(value: any, originalValue: any) {
	if (typeof originalValue === 'string' && originalValue.trim() === '') {
		return undefined;
	}
	return value;
}

/**
 * Retrieves the country name corresponding to a given ISO 3166-1 alpha-2 country code.
 *
 * @param code - he two-letter country code (ISO 3166-1 alpha-2).
 * @returns The localized name of the country in English, or `undefined` if the code is invalid.
 *
 * @example
 * ```typescript
 * const countryName = getCountryNameFromCode("US");
 * console.log(countryName); // Output: "United States"
 * ```
 */
export function getCountryNameFromCode(code?: string) {
	if (!code) {
		return undefined;
	}
	code = code.toUpperCase();
	const country = new Intl.DisplayNames(['en'], { type: 'region' });

	try {
		return country.of(code);
	}
	catch {
		return 'N/A';
	}
}

/**
 * Creates a deep clone of the provided object.
 *
 * This function serializes the input object to JSON and then parses it back,
 * effectively creating a new object with the same structure and values.
 * Note that this approach may not handle certain data types (e.g., functions,
 * `undefined`, `Date`, `Map`, `Set`, etc.) correctly.
 *
 * @typeParam T - The type of the object to clone.
 * @param obj - The object to be deeply cloned.
 * @returns A new object that is a deep clone of the input object.
 */
export function cloneDeep<T>(obj: T): T {
	if (obj === null || typeof obj !== 'object') { return obj; }
	return JSON.parse(JSON.stringify(obj));
}

/**
 * Compares two objects or values for deep equality.
 *
 * @param a - The first value to compare. Can be of any type.
 * @param b - The second value to compare. Can be of any type.
 * @returns `true` if the two values are deeply equal, otherwise `false`.
 *
 * @remarks
 * - If both values are strictly equal (`===`), the function returns `true`.
 * - If either value is not an object or is `null`, the function returns `false`.
 * - The function performs a deep comparison of object properties, including nested objects.
 * - If the objects have different numbers of keys, the function returns `false`.
 */
export function objectCompare(a: any, b: any) {
	if (a === b) {
		return true;
	}
	if (typeof a !== 'object' || typeof b !== 'object') {
		return false;
	}
	if (a === null || b === null) {
		return false;
	}
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) {
		return false;
	}
	for (const key of keysA) {
		if (!keysB.includes(key) || !objectCompare(a[key], b[key])) {
			return false;
		}
	}
	return true;
}

/**
 * A utility type that represents a readonly reference to a value of type `T`.
 * This ensures that the reference cannot be modified, providing immutability.
 *
 * @template T - The type of the value being referenced.
 */
export type ReadonlyRef<T> = Readonly<Ref<T>>;

/**
 * Generates a unique identifier (ID) using the current timestamp and a random number.
 *
 * @returns A string representing the unique ID.
 *
 * @remarks
 * The ID is generated by concatenating the current timestamp (in milliseconds since the Unix epoch)
 * converted to a base-36 string with a random number (also in base-36) padded to 12 characters.
 */
export function getId() {
	return Date.now().toString(36) + Math.random().toString(36).substring(2, 12).padStart(12, '0');
}

/**
 * Removes the protocol (http:// or https://) from a given URL.
 * @param url - The URL from which to remove the protocol.
 * @returns The URL without the protocol (http:// or https://).
 */
export function trimUrlProtocol(url?: string) {
	if (!url) {
		return url;
	}

	// eslint-disable-next-line regexp/no-dupe-disjunctions
	return url.replace(/^(https?:\/\/|http:\/\/)/, '');
}

// Helper type to safely extract the third parameter type of getCachedData if it exists
type GetCachedDataType<T> = T extends (...args: any) => any ? Parameters<T>[2] : never;
type AsyncDataCtx = GetCachedDataType<AsyncDataOptions<any>['getCachedData']>;

export function getCachedAsyncData(key: string, nuxtApp: NuxtApp, ctx: AsyncDataCtx) {
	if (ctx.cause === 'refresh:manual') {
		return;
	}

	const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key];

	if (!data) {
		return;
	}

	return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
}

/**
 * Converts a string to a human-readable format.
 * @param string The input string to convert.
 * @returns The human-readable string.
 */
export function convertToHumanReadable(string?: string) {
	if (!string) {
		return '';
	}

	return string
		.replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase
		.replace(/_/g, ' ') // snake_case
		.replace(/-/g, ' ') // kebab-case
		.replace(/\s+/g, ' ') // multiple spaces
		.trim();
}

export function exportStringToFile(data: string, filename: string, type: 'csv' | 'txt' = 'txt') {
	const blob = new Blob([data], { type: type === 'csv' ? 'text/csv;charset=utf-8;' : 'text/plain;charset=utf-8;' });
	downloadFile(blob, filename);
}

export function downloadFile(blob: Blob, filename: string) {
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

/**
 * Deletes all properties from the target object whose keys start with the specified prefix.
 *
 * @param target - The object from which to delete matching properties.
 * @param startsWith - The prefix to match against property keys. Defaults to 'client'.
 *
 * @example
 * ```typescript
 * const obj = { clientId: 1, clientName: 'test', serverId: 2 };
 * deleteKeysStartingWith(obj, 'client');
 * // obj is now { serverId: 2 }
 * ```
 */
export function deleteKeysStartingWith(target: any, startsWith: string = 'client') {
	const clientKeys = Object.keys(target).filter(key => key.startsWith(startsWith));
	clientKeys.forEach((key) => {
		delete target[key];
	});
}

export function tryJSONParse<T>(input?: string | null): T | null {
	if (!input) {
		return null;
	}

	try {
		return JSON.parse(input) as T;
	}
	catch (error) {
		console.error('Failed to parse JSON:', error);
		return null;
	}
}

export function waitFor(ms: number = 5000) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

export function formatBytes(bytes: number, decimals?: number) {
	if (!+bytes) {
		return '0 Bytes';
	}

	const k = 1024;
	const dm = decimals ?? 0;
	const sizes = ['Bytes', 'kB', 'MB', 'GB', 'TB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Normalizes a string by removing diacritics, apostrophes, and extra whitespace.
 *
 * This function performs the following transformations:
 * - Decomposes Unicode characters (NFD normalization)
 * - Removes diacritic marks (accents, umlauts, etc.)
 * - Removes all types of apostrophes (straight and curly quotes)
 * - Normalizes multiple consecutive spaces to single spaces
 * - Trims leading and trailing whitespace
 * - Converts the result to lowercase
 *
 * @param input - The string to normalize
 * @returns The normalized string
 *
 * @example
 * ```typescript
 * normalizeString("Café d'été"); // Returns: "cafe dete"
 * normalizeString("  Hello   World  "); // Returns: "hello world"
 * normalizeString("Naïve résumé"); // Returns: "naive resume"
 * ```
 */
export function normalizeString(input: string): string {
	return input
		.normalize('NFD') // Decompose combined letters into letter + diacritic
		.replace(/[\u0300-\u036F]/g, '') // Remove diacritic marks
		.replace(/[\u2018\u2019']/g, '') // Remove all apostrophe types (curly and straight)
		.replace(/\s+/g, ' ') // Normalize multiple spaces to single space
		.trim() // Remove leading/trailing whitespace
		.toLowerCase(); // Convert to lowercase
}

/**
 * Loads an image file and returns a promise that resolves with an HTMLImageElement.
 *
 * @param file - The image file to load
 * @returns A promise that resolves with the loaded HTMLImageElement
 * @throws Will reject if the file cannot be read or the image cannot be loaded
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file); // SAFE for huge files
		const img = new Image();

		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};

		img.onerror = (err) => {
			URL.revokeObjectURL(url);
			reject(err);
		};

		img.src = url;
	});
}

export function formatPixelDimensions(width?: number, height?: number): string {
	if (!width || !height) {
		return '';
	}
	return `${width.toFixed(0)} x ${height.toFixed(0)} Pixel (${((width * height) / 1000000).toFixed(2)} Mpx)`;
}

/**
 * Returns a user-friendly label for a given Excellence Program level.
 *
 * @param level - The raw ExcellenceProgramLevel value received from the API
 * @returns A readable label mapped to the provided program level
 */
export const excellenceProgramLabels: Record<ExcellenceProgramLevel, string> = {
	PresidentClub: 'President\'s Club',
	Gold: 'Gold',
	Platinum: 'Platinum',
	HCircle: 'H-Circle',
};

export function getExcellenceProgramLabel(level?: ExcellenceProgramLevel): string {
	if (!level) {
		return '';
	}
	return excellenceProgramLabels[level] ?? level;
}

/**
 * Logs messages to the console with a styled prefix, only in development mode.
 *
 * @remarks
 * This function wraps `console.log` and adds a colored prefix "🎨 [HM_LOG]"
 * to distinguish application logs. It only outputs when `import.meta.env.DEV`
 * is truthy, preventing console output in production builds.
 *
 * @param args - The values to be logged to the console
 *
 * @example
 * ```typescript
 * consoleLog('User logged in', { userId: 123 });
 * In dev mode, outputs: 🎨 [HM_LOG] User logged in { userId: 123 }
 * ```
 */
export function consoleLog(...args: unknown[]) {
	if (import.meta.env.DEV) {
		// eslint-disable-next-line no-console
		console.log('%c🎨 [HM_LOG]', 'color: #4CAF50; font-weight: bold', ...args);
	}
}
